import * as R from 'ramda';

import { getFlexSearchInstance } from './utils.ts';

const DEFAULT_RESULTS_LIMIT = 5000;

/**
 * Moves exact name matches to the front and tags them with `isExactNameMatch`.
 * FlexSearch also scores keyword hits, so a keyword match can outrank a POI whose
 * name *is* the query; this fixes that. Stable sort keeps FlexSearch order within
 * each group; matched POIs are shallow-copied so the shared store isn't mutated.
 */
function promoteExactMatches(results, exactIdSet) {
  if (!exactIdSet.size) {
    return results;
  }
  return results
    .map((poi, index) => {
      const exact = exactIdSet.has(Number(poi.poiId));
      return { poi: exact ? { ...poi, isExactNameMatch: true } : poi, index, exact };
    })
    .sort((a, b) => Number(b.exact) - Number(a.exact) || a.index - b.index)
    .map(entry => entry.poi);
}

export default function createPOISearch(pois, lang) {
  const index = getFlexSearchInstance({ lang });

  // index.addMatcher({
  //   '[\'.,]': ''
  // })

  prepareIndexEntries(pois).forEach(([id, content]) => index.add(id, content));

  function prepareIndexEntries(pois) {
    return Object.values(pois).map(poi => {
      const { poiId, category = '', name, staticName, keywords = [], roomId = '' } = poi;
      const grabTags = R.path(['dynamicData', 'grab', 'tags'], poi) || [];
      const searchKeywords = keywords.filter(R.prop('isUserSearchable')).map(R.prop('name'));
      // Include staticName in index only when it differs from current name (during override)
      const staticNamePart = staticName && staticName !== name ? staticName : '';
      const content = `${name} ${staticNamePart} ${category.split('.').join(' ')} ${roomId} ${searchKeywords.join(' ')} ${grabTags.join(' ')}`;
      return [Number(poiId), content];
    });
  }

  /**
   * Ids of POIs whose name/staticName exactly equals the query. Scans the full
   * set so exact matches dropped by FlexSearch's limit are still found.
   */
  function findExactNameMatchIds(normalizedQuery) {
    const ids = new Set();
    Object.values(pois).forEach(poi => {
      const isExact = [poi.name, poi.staticName].filter(Boolean).some(name => name.toLowerCase() === normalizedQuery);
      if (isExact) {
        ids.add(Number(poi.poiId));
      }
    });
    return ids;
  }

  function search(queryParams) {
    const options = { ...queryParams };
    if (!options.limit) {
      // sometimes limit is NaN or undefined!
      options.limit = DEFAULT_RESULTS_LIMIT;
    }
    const ids = index.search(options);
    const normalizedQuery = (options.query ?? '').toLowerCase();
    const exactIdSet = normalizedQuery ? findExactNameMatchIds(normalizedQuery) : new Set();
    // Add exact matches FlexSearch's limit may have dropped, promote them, then trim.
    const missingExactIds = [...exactIdSet].filter(id => !ids.includes(id));
    const results = Object.values(R.pick(ids.concat(missingExactIds), pois));
    return promoteExactMatches(results, exactIdSet).slice(0, options.limit);
  }

  function updateMultiple(pois) {
    prepareIndexEntries(pois).forEach(([id, content]) => index.update(id, content));
  }

  return { search, updateMultiple };
}
